import { SUMMARY_FIELD_KEYS, type SummaryBuiltinCard, type SummaryFieldKey } from '@ehr/shared';

/**
 * ehr#230 - the Summary tab's data helpers (pure, no React).
 *
 * `summaryFromDemographics` is the ONLY place the Summary reads the Demographics record
 * (handoff step 2): the header strip / client-info values and the seven built-in cards.
 * Form answers arrive from GET /contacts/:id/summary as `SummaryFormFeed`s.
 */

/** The drawer's demographics record: section (or `section#n` extra entry) -> field -> value. */
export type Demographics = Record<string, Record<string, string>>;

export type SummaryField = { label: string; value: string; full?: boolean };
export type BuiltinCardView = {
  fields: SummaryField[];
  /** Labels of the card's usual fields that are blank ("2 fields empty: race, ethnicity"). */
  empty: string[];
  /** Shown instead of fields when the section holds nothing at all. */
  nothing?: string;
  /** The Demographics section the card's Edit link opens. */
  section: string;
  /** The Edit link's label (Family & Contacts reads "Add" when empty). */
  link: string;
};

export type SummaryFormFeed = {
  key: string;
  name: string;
  fields: { id: string; label: string | null }[];
  entries: { at: string; source: 'staff' | 'portal'; values: Record<string, string> }[];
};

const s = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
const join = (...parts: string[]) => parts.map(s).filter(Boolean).join(' ');

/** A stored list (JSON array string or plain text) as "a, b". */
export function listValue(v: string | undefined): string {
  const t = s(v);
  if (!t.startsWith('[')) return t;
  try {
    const arr = JSON.parse(t) as unknown;
    return Array.isArray(arr)
      ? arr
          .map((x) => String(x).trim())
          .filter(Boolean)
          .join(', ')
      : t;
  } catch {
    return t;
  }
}

/** "03-14-1992" from an ISO date; '' when blank or invalid. */
export function fmtDate(iso: string | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s(iso));
  return m ? `${m[2]}-${m[3]}-${m[1]}` : '';
}

/** Whole years from an ISO date of birth to `today`; null when blank, invalid or future. */
export function ageOn(dobIso: string | undefined, today: Date): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s(dobIso));
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  let age = today.getFullYear() - y;
  if (today.getMonth() + 1 < mo || (today.getMonth() + 1 === mo && today.getDate() < d)) age--;
  return age >= 0 && age < 150 ? age : null;
}

/** Every entry of a repeatable section: the base one, then `section#n` extras in order. */
export function entriesOf(data: Demographics, section: string): Record<string, string>[] {
  const extras = Object.keys(data)
    .filter((k) => k.startsWith(`${section}#`))
    .sort((a, b) => Number(a.split('#')[1]) - Number(b.split('#')[1]));
  return [data[section] ?? {}, ...extras.map((k) => data[k] ?? {})];
}
const hasAny = (r: Record<string, string> | undefined) =>
  !!r && Object.entries(r).some(([k, v]) => !k.startsWith('_') && s(v) && v !== 'false');

function addressOf(r: Record<string, string>): string {
  const line = [s(r.address1), s(r.address2)].filter(Boolean).join(', ');
  const cityLine = [s(r.city), [s(r.state), s(r.zip)].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  return [line, cityLine].filter(Boolean).join(', ');
}
const personName = (r: Record<string, string>) =>
  join(r.salutation ?? '', r.firstName ?? '', r.middleName ?? '', r.lastName ?? '', r.suffix ?? '');

/** The care team member marked primary clinician ("Priya Patel, LCSW"); '' when none. */
export function primaryClinician(data: Demographics): string {
  const team = entriesOf(data, 'careteam').filter(hasAny);
  const p = team.find((r) => r.primary === 'true');
  if (!p) return '';
  const role = s(p.role) === 'Other' ? s(p.roleOther) : s(p.role);
  return [join(p.firstName ?? '', p.lastName ?? ''), role].filter(Boolean).join(', ');
}

/** Values for the header strip / client-info fields (blank '' when not on file). */
export function clientFieldValues(
  data: Demographics,
  opts: { balanceCents?: number | null; today?: Date },
): Record<SummaryFieldKey, string> {
  const c = data.contact ?? {};
  const id = data.identity ?? {};
  const ins = data.insurance ?? {};
  const em = data.emergency ?? {};
  const age = ageOn(id.dob, opts.today ?? new Date());
  const out = Object.fromEntries(SUMMARY_FIELD_KEYS.map((k) => [k, ''])) as Record<
    SummaryFieldKey,
    string
  >;
  out.pref = s(c.preferredName);
  out.age = age === null ? '' : `${age} (${fmtDate(id.dob)})`;
  out.pron = s(c.pronouns) === 'Other' ? s(c.pronounsOther) : s(c.pronouns);
  out.payor = s(ins.payer);
  out.member = s(ins.memberId);
  out.group = s(ins.groupNum);
  out.phone = s(c.mobilePhone);
  out.email = s(c.email);
  out.clin = primaryClinician(data);
  out.emer = [join(em.firstName ?? '', em.lastName ?? ''), s(em.phone)].filter(Boolean).join(', ');
  out.lang = s(id.language) === 'Other' ? s(id.languageOther) : s(id.language);
  out.bal =
    opts.balanceCents == null
      ? ''
      : (opts.balanceCents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  return out;
}

/** Build a card from [label, value, full?] rows: filled ones show, blank ones are listed. */
function card(
  rows: [string, string, boolean?][],
  section: string,
  extra: Partial<BuiltinCardView> = {},
): BuiltinCardView {
  const fields: SummaryField[] = [];
  const empty: string[] = [];
  for (const [label, value, full] of rows) {
    if (value) fields.push({ label, value, ...(full ? { full: true } : {}) });
    else empty.push(label === label.toUpperCase() ? label : label.toLowerCase()); // "MRN" stays
  }
  return { fields, empty, section, link: 'Edit', ...extra };
}

/** The seven built-in Demographics cards. */
export function builtinCards(
  data: Demographics,
  today: Date = new Date(),
): Record<SummaryBuiltinCard, BuiltinCardView> {
  const c = data.contact ?? {};
  const id = data.identity ?? {};
  const g = data.guardian ?? {};
  const em = data.emergency ?? {};
  const ins = data.insurance ?? {};
  const gu = data.guarantor ?? {};
  const age = ageOn(id.dob, today);

  const guardianCard = hasAny({ ...g, isMinor: '' })
    ? card(
        [
          ['Name', personName(g)],
          ['Relationship', s(g.relationship)],
          ['Mobile phone', s(g.mobilePhone)],
          ['Email', s(g.email)],
        ],
        'guardian',
      )
    : {
        fields: [],
        empty: [],
        section: 'guardian',
        link: 'Add',
        nothing: 'Nothing on file. Only needed for clients under 18.',
      };

  const team = entriesOf(data, 'careteam').filter(hasAny);
  const careCard: BuiltinCardView = team.length
    ? {
        fields: team.map((r) => {
          const role = s(r.role) === 'Other' ? s(r.roleOther) : s(r.role);
          const label = r.primary === 'true' ? 'Primary clinician' : role || 'Care team member';
          const name = join(r.firstName ?? '', r.lastName ?? '');
          const tail = r.primary === 'true' ? role : s(r.organization);
          return { label, value: [name, tail].filter(Boolean).join(', ') || '—' };
        }),
        empty: [],
        section: 'careteam',
        link: 'Edit',
      }
    : { fields: [], empty: [], section: 'careteam', link: 'Add', nothing: 'No care team on file.' };

  const second = entriesOf(data, 'insurance').slice(1).find(hasAny);
  const insCard = card(
    [
      ['Primary payor', s(ins.payer)],
      ['Member ID', s(ins.memberId)],
      ['Group number', s(ins.groupNum)],
      ['Plan type', s(ins.planType)],
      ['Policy holder', s(ins.subscriberRel)],
      ['Secondary payor', second ? s(second.payer) || 'On file' : ''],
    ],
    'insurance',
  );

  const sameAsClient = gu.sameAsClient === 'true';
  return {
    contact: card(
      [
        ['Name', personName(c)],
        ['Preferred name', s(c.preferredName)],
        ['Mobile phone', s(c.mobilePhone)],
        ['Home phone', s(c.phone)],
        ['Work phone', s(c.workPhone)],
        ['Email', s(c.email)],
        ['Home address', addressOf(c), true],
      ],
      'contact',
    ),
    demo: card(
      [
        ['Date of birth', age === null ? fmtDate(id.dob) : `${fmtDate(id.dob)} (${age})`],
        ['Pronouns', s(c.pronouns) === 'Other' ? s(c.pronounsOther) : s(c.pronouns)],
        [
          'Gender identity',
          s(c.genderIdentity) === 'Other' ? s(c.genderIdentityOther) : s(c.genderIdentity),
        ],
        ['Languages', s(id.language) === 'Other' ? s(id.languageOther) : s(id.language)],
        ['Marital status', s(id.maritalStatus)],
        ['Race / ethnicity', listValue(id.race)],
        ['Employment', s(data.employment?.status)],
        ['MRN', s(id.mrn)],
      ],
      'identity',
    ),
    guardian: guardianCard,
    emergency: hasAny(em)
      ? card(
          [
            ['Name', personName(em)],
            ['Relationship', s(em.relationship)],
            ['Phone', s(em.phone)],
            ['Email', s(em.email)],
          ],
          'emergency',
        )
      : {
          fields: [],
          empty: [],
          section: 'emergency',
          link: 'Add',
          nothing: 'No emergency contact on file.',
        },
    care: careCard,
    insurance: hasAny(ins)
      ? insCard
      : {
          fields: [],
          empty: [],
          section: 'insurance',
          link: 'Add',
          nothing: 'No insurance on file.',
        },
    guarantor: hasAny(gu)
      ? card(
          sameAsClient
            ? [['Responsible party', 'Self (same as client)']]
            : [
                ['Responsible party', personName(gu)],
                ['Relationship', s(gu.relationship)],
                ['Phone', s(gu.mobilePhone)],
                ['Billing address', addressOf(gu), true],
              ],
          'guarantor',
        )
      : {
          fields: [],
          empty: [],
          section: 'guarantor',
          link: 'Add',
          nothing: 'No guarantor on file.',
        },
  };
}

/** True when nothing at all is on file: no Demographics answers and no form answers. */
export function nothingOnFile(data: Demographics, forms: SummaryFormFeed[]): boolean {
  const any = Object.entries(data).some(([sec, r]) => !sec.startsWith('reminders') && hasAny(r));
  return !any && !forms.some((f) => f.entries.length);
}

/** A form field's latest answer and the earlier ones ("1 earlier"). */
export function fieldAnswers(
  feed: SummaryFormFeed | undefined,
  fieldId: string,
): {
  latest: { value: string; at: string; source: string } | null;
  earlier: { value: string; at: string }[];
} {
  const hits = (feed?.entries ?? [])
    .filter((e) => e.values[fieldId] !== undefined)
    .map((e) => ({ value: e.values[fieldId]!, at: e.at, source: e.source }));
  return {
    latest: hits[0] ?? null,
    earlier: hits.slice(1).map(({ value, at }) => ({ value, at })),
  };
}

/** "10-02-2026" from an ISO time. */
export function fmtWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}-${p(d.getDate())}-${d.getFullYear()}`;
}
